import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router";

import { Card, EmptyState, LoadingBlock, PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { formatDate } from "../../lib/format";
import type { Page } from "../../lib/types";
import { FilesSummary } from "../files/FilesPage";
import { DueText } from "../orders/OrderBits";
import type { OrderSummary } from "../orders/types";

const linkBtn =
  "inline-flex min-h-11 items-center rounded-md bg-brand-600 px-4 font-semibold text-white hover:bg-brand-700";

/** Staff and administrators use the staff workspace instead of the customer dashboard. */
export function DashboardPage() {
  const { user } = useAuth();
  if (user?.is_staff) return <Navigate to="/staff" replace />;
  return <CustomerDashboard />;
}

function CustomerDashboard() {
  const { user } = useAuth();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const orders = useQuery({
    queryKey: ["orders", "recent"],
    queryFn: () => api.get<Page<OrderSummary>>("/orders?page_size=5"),
  });
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Welcome, ${user?.first_name ?? ""}`}
        description="Here is a summary of your work with Bill's."
        actions={
          <Link to="/orders/new" className={linkBtn}>
            New order TO Bill's
          </Link>
        }
      />
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Recent orders TO Bill's</h2>
          <Link to="/orders" className="text-brand-700 underline">
            All orders
          </Link>
        </div>
        {orders.isLoading ? (
          <LoadingBlock />
        ) : orders.data && orders.data.items.length > 0 ? (
          <ul className="divide-y divide-line">
            {orders.data.items.map((o) => (
              <li
                key={o.id}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  <Link to={`/orders/${o.id}`} className="font-semibold text-brand-700 underline">
                    {o.order_number}
                  </Link>{" "}
                  · {o.project_name_number}
                  <span className="block text-sm text-ink-muted">
                    Submitted {formatDate(o.submitted_at, tz)}
                  </span>
                </span>
                <span className="text-sm">
                  Due <DueText order={o} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No orders yet">Start a new order to send files to Bill's.</EmptyState>
        )}
      </Card>
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Files FROM Bill's</h2>
          <Link to="/files" className="text-brand-700 underline">
            All files
          </Link>
        </div>
        <FilesSummary />
      </Card>
    </div>
  );
}
